<template>
  <aside
    v-if="isVisible"
    class="w-full lg:w-[480px] bg-card rounded-2xl shadow-sm border border-border flex flex-col overflow-hidden animate-in slide-in-from-right-10 duration-500 h-full lg:h-auto"
  >
    <!-- Sidebar Header -->
    <div
      class="h-20 border-b border-border flex items-center justify-between px-6 shrink-0"
    >
      <div class="flex items-center gap-3">
        <button
          v-if="isCreating"
          @click="goBackToList"
          class="p-2 -ml-2 hover:bg-muted/50 rounded-lg transition-colors"
          title="Back to notes"
        >
          <ArrowLeft class="w-5 h-5 text-muted-foreground" />
        </button>
        <h2 class="font-bold text-[20px] text-foreground shrink-0">Notes</h2>
      </div>
      <button
        @click="$emit('close')"
        class="p-2 hover:bg-muted/50 rounded-lg transition-colors"
        title="Close Notes Sidebar"
      >
        <X class="w-6 h-6 text-muted-foreground" />
      </button>
    </div>

    <!-- Notes Content -->
    <div class="p-4 lg:p-6 flex-1 overflow-y-auto space-y-6">
      <!-- Create Note Form -->
      <div
        v-if="isCreating"
        class="h-full flex flex-col gap-5 animate-in fade-in duration-300"
      >
        <!-- Formatting Toolbar & Save Button -->
        <div class="flex items-center justify-between w-full">
          <div
            class="flex items-center gap-1 sm:gap-2 text-muted-foreground/60"
          >
            <button
              @click="toggleBold"
              :class="[
                'p-1.5 rounded transition-all',
                editor?.isActive('bold')
                  ? 'text-primary bg-primary/10'
                  : 'hover:text-foreground hover:bg-muted/30',
              ]"
            >
              <Bold class="w-5 h-5 stroke-[1.5]" />
            </button>
            <button
              @click="toggleItalic"
              :class="[
                'p-1.5 rounded transition-all',
                editor?.isActive('italic')
                  ? 'text-primary bg-primary/10'
                  : 'hover:text-foreground hover:bg-muted/30',
              ]"
            >
              <Italic class="w-5 h-5 stroke-[1.5]" />
            </button>
            <button
              @click="toggleBulletList"
              :class="[
                'p-1.5 rounded transition-all',
                editor?.isActive('bulletList')
                  ? 'text-primary bg-primary/10'
                  : 'hover:text-foreground hover:bg-muted/30',
              ]"
            >
              <List class="w-5 h-5 stroke-[1.5]" />
            </button>
            <button
              @click="setTextAlign('left')"
              :class="[
                'p-1.5 rounded transition-all',
                editor?.isActive({ textAlign: 'left' })
                  ? 'text-primary bg-primary/10'
                  : 'hover:text-foreground hover:bg-muted/30',
              ]"
            >
              <AlignLeft class="w-5 h-5 stroke-[1.5]" />
            </button>
            <button
              @click="setTextAlign('center')"
              :class="[
                'p-1.5 rounded transition-all',
                editor?.isActive({ textAlign: 'center' })
                  ? 'text-primary bg-primary/10'
                  : 'hover:text-foreground hover:bg-muted/30',
              ]"
            >
              <AlignCenter class="w-5 h-5 stroke-[1.5]" />
            </button>
            <button
              @click="setTextAlign('right')"
              :class="[
                'p-1.5 rounded transition-all',
                editor?.isActive({ textAlign: 'right' })
                  ? 'text-primary bg-primary/10'
                  : 'hover:text-foreground hover:bg-muted/30',
              ]"
            >
              <AlignRight class="w-5 h-5 stroke-[1.5]" />
            </button>

            <div class="relative">
              <button
                @click="toggleLinkInput"
                :class="[
                  'p-1.5 hover:text-foreground hover:bg-muted/30 rounded transition-all',
                  showLinkInput ? 'text-primary bg-primary/10' : '',
                ]"
              >
                <Link2 class="w-5 h-5 stroke-[1.5]" />
              </button>

              <!-- Custom Link Input Popup -->
              <div
                v-if="showLinkInput"
                v-click-outside="() => (showLinkInput = false)"
                class="absolute top-full left-0 mt-2 p-2 bg-card border border-border rounded-xl shadow-xl z-50 flex items-center gap-2 animate-in slide-in-from-top-2 duration-200 min-w-[240px]"
              >
                <input
                  v-model="linkInputUrl"
                  type="text"
                  placeholder="https://..."
                  class="flex-1 bg-muted/30 border border-border/50 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-primary/50"
                  @keydown.enter.prevent="confirmLink"
                  @keydown.esc="showLinkInput = false"
                  ref="linkInput"
                />
                <button
                  @click="confirmLink"
                  class="p-1.5 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
                  title="Confirm"
                >
                  <Check class="w-4 h-4" />
                </button>
                <button
                  v-if="editor?.isActive('link')"
                  @click="removeLink"
                  class="p-1.5 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"
                  title="Remove Link"
                >
                  <Trash2 class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <button
            @click="saveNote"
            class="px-5 py-2 sm:px-6 sm:py-2.5 bg-[#3A3F47] dark:bg-foreground dark:text-background text-white rounded-full text-[14px] font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            Save Note
          </button>
        </div>

        <!-- Content Area -->
        <div
          class="flex-1 min-h-[350px] border border-border/80 rounded-[12px] p-5 relative group bg-transparent overflow-y-auto"
        >
          <editor-content
            :editor="editor"
            class="editor-content w-full h-full bg-transparent text-[16px] text-foreground/85 outline-none leading-relaxed"
            @keydown.meta.k.prevent="toggleLinkInput"
            @keydown.ctrl.k.prevent="toggleLinkInput"
          />
          <Maximize2
            class="absolute bottom-4 right-4 w-5 h-5 text-muted-foreground/30 hover:text-muted-foreground/60 cursor-pointer"
          />
        </div>
      </div>

      <!-- Notes List -->
      <template v-else>
        <div
          v-if="!notes.length"
          class="flex flex-col items-center justify-center py-20 text-center"
        >
          <div
            class="w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mb-6"
          >
            <StickyNote class="w-8 h-8 text-muted-foreground/30" />
          </div>
          <p class="text-[18px] font-bold text-foreground mb-2">No notes yet</p>
          <p class="text-muted-foreground leading-relaxed text-sm">
            Capture your thoughts and takeaways while you learn.
          </p>
        </div>

        <div
          v-for="note in notes"
          :key="note.id"
          class="p-5 rounded-2xl border border-border hover:border-primary/30 hover:bg-muted/10 transition-all cursor-pointer group shadow-sm bg-card relative"
          @click="selectNote(note)"
        >
          <!-- Pin/Delete Actions -->
          <div
            class="absolute top-4 right-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
          >
            <button
              @click.stop="$emit('toggle-pin', note)"
              class="p-1.5 rounded-lg hover:bg-muted/50 transition-colors"
              :class="
                note.is_pinned ? 'text-primary' : 'text-muted-foreground/40'
              "
              :title="note.is_pinned ? 'Unpin' : 'Pin'"
            >
              <Pin
                class="w-4 h-4"
                :class="{ 'fill-primary': note.is_pinned }"
              />
            </button>
            <button
              @click.stop="$emit('delete-note', note)"
              class="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground/40 hover:text-red-500 transition-colors"
              title="Delete Note"
            >
              <Trash2 class="w-4 h-4" />
            </button>
          </div>

          <div class="flex justify-between items-start mb-2 pr-32">
            <h3 class="text-[16px] font-bold text-foreground truncate">
              {{ note.title }}
            </h3>
            <span
              class="text-[11px] text-muted-foreground font-bold shrink-0"
              >{{ formatDate(note.created_at) }}</span
            >
          </div>
          <p
            class="text-[14px] text-muted-foreground/80 line-clamp-3 leading-relaxed"
          >
            {{ stripHtml(note.content) }}
          </p>
        </div>
      </template>
    </div>
  </aside>
</template>

<script>
import {
  X,
  Bold,
  Italic,
  List,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link2,
  Maximize2,
  StickyNote,
  Check,
  Pin,
  Trash2,
  ArrowLeft,
} from "lucide-vue-next";
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";

export default {
  name: "NotesSidebar",
  components: {
    X,
    Bold,
    Italic,
    List,
    AlignLeft,
    AlignCenter,
    AlignRight,
    Link2,
    Maximize2,
    StickyNote,
    Check,
    Pin,
    Trash2,
    ArrowLeft,
    EditorContent,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    notes: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "save-note", "delete-note", "toggle-pin"],
  data() {
    return {
      editor: null,
      isCreating: false,
      activeNoteId: null,
      newNote: {
        title: "",
        content: "",
      },
      showLinkInput: false,
      linkInputUrl: "",
      currentEditingNoteId: null, // Added for tracking the note being edited
    };
  },
  mounted() {
    this.initEditor();
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
  methods: {
    initEditor() {
      this.editor = new Editor({
        extensions: [
          StarterKit.configure({
            heading: {
              levels: [1],
            },
          }),
          Link.configure({
            openOnClick: false,
          }),
          Placeholder.configure({
            placeholder: "It is important for products...",
          }),
          TextAlign.configure({
            types: ["heading", "paragraph"],
          }),
        ],
        content: "<h1></h1>",
        onUpdate: ({ editor }) => {
          this.enforceFirstLineHeading(editor);
          this.handleAutoCapitalization(editor);
          this.newNote.content = editor.getHTML();
        },
      });
    },
    enforceFirstLineHeading(editor) {
      if (!editor.isFocused) return;
      const { doc } = editor.state;
      const firstChild = doc.firstChild;

      if (
        !firstChild ||
        firstChild.type.name !== "heading" ||
        firstChild.attrs.level !== 1
      ) {
        editor.chain().setNodeSelection(0).setHeading({ level: 1 }).run();
      }
    },
    handleAutoCapitalization(editor) {
      if (!editor.isFocused) return;
      const { doc, selection } = editor.state;
      const { from } = selection;

      const firstNode = doc.firstChild;
      if (firstNode && firstNode.type.name === "heading" && from <= 10) {
        const text = firstNode.textContent;
        if (text.length === 1) {
          const char = text[0];
          if (char === char.toLowerCase() && char !== char.toUpperCase()) {
            editor
              .chain()
              .insertContentAt({ from: 1, to: 2 }, char.toUpperCase(), {
                updateSelection: false,
              })
              .run();
          }
        }
      }
    },
    startCreatingNote() {
      this.isCreating = true;
      this.currentEditingNoteId = null;
      this.$nextTick(() => {
        if (this.editor) {
          this.editor.commands.setContent("<h1></h1>");
          this.editor.commands.focus();
        }
      });
    },
    selectNote(note) {
      this.currentEditingNoteId = note.id;
      this.isCreating = true;
      if (this.editor) {
        // Prepend title as H1 and then add content
        const content = `<h1>${note.title}</h1>${note.content || ""}`;
        this.editor.commands.setContent(content);
        this.editor.commands.focus();
      }
    },
    goBackToList() {
      // Save changes before going back
      if (this.editor && !this.editor.isEmpty) {
        this.saveNote(); // Changed from emitNote() to saveNote() as emitNote() was not defined
      }
      this.currentEditingNoteId = null;
      this.isCreating = false;
      if (this.editor) {
        this.editor.commands.setContent("<h1></h1>", false); // Reset editor content
      }
    },
    cancelCreate() {
      this.isCreating = false;
      this.currentEditingNoteId = null;
      this.newNote = { title: "", content: "" };
      this.showLinkInput = false;
      this.linkInputUrl = "";
    },
    handleHeaderClose() {
      if (this.isCreating) {
        this.cancelCreate();
      } else {
        this.$emit("close");
      }
    },
    toggleBold() {
      this.editor.chain().focus().toggleBold().run();
    },
    toggleItalic() {
      this.editor.chain().focus().toggleItalic().run();
    },
    toggleBulletList() {
      this.editor.chain().focus().toggleBulletList().run();
    },
    setTextAlign(alignment) {
      this.editor.chain().focus().setTextAlign(alignment).run();
    },
    toggleLinkInput() {
      this.showLinkInput = !this.showLinkInput;
      if (this.showLinkInput) {
        // Get current link if any
        this.linkInputUrl = this.editor.getAttributes("link").href || "";
        this.$nextTick(() => {
          this.$refs.linkInput?.focus();
          this.$refs.linkInput?.select();
        });
      }
    },
    removeLink() {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
      this.showLinkInput = false;
      this.linkInputUrl = "";
    },
    confirmLink() {
      if (this.linkInputUrl) {
        let url = this.linkInputUrl;
        if (!url.startsWith("http")) {
          url = "https://" + url;
        }
        this.editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: url })
          .run();
      } else {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
      }
      this.showLinkInput = false;
      this.linkInputUrl = "";
    },
    saveNote() {
      if (!this.editor || this.editor.isEmpty) return;

      const html = this.editor.getHTML();
      // Extract title from H1
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const h1 = tempDiv.querySelector("h1");
      const title = h1 ? h1.innerText : "Untitled Note";

      // Content is everything except the first H1
      if (h1) h1.remove();
      const content = tempDiv.innerHTML;

      this.$emit("save-note", {
        id: this.currentEditingNoteId,
        title: title || "Untitled Note",
        content: content || html,
        isRichText: true,
      });
      this.isCreating = false;
      this.currentEditingNoteId = null;
      this.newNote = { title: "", content: "" };
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    },
    stripHtml(html) {
      if (!html) return "";
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      return tempDiv.innerText || tempDiv.textContent || "";
    },
  },
  directives: {
    "click-outside": {
      beforeMount(el, binding) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.addEventListener("click", el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
};
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

:deep(.editor-content .tiptap) {
  outline: none;
  min-height: 350px;
}

:deep(.editor-content h1) {
  font-size: 24px;
  font-weight: 800;
  color: var(--foreground);
  margin-bottom: 20px;
  line-height: 1.2;
}

:deep(.editor-content p) {
  font-size: 16px;
  color: var(--foreground);
  opacity: 0.85;
  line-height: 1.6;
}

:deep(.editor-content ul) {
  padding-left: 24px;
  margin: 16px 0;
  list-style-type: disc !important;
}

:deep(.editor-content ol) {
  padding-left: 24px;
  margin: 16px 0;
  list-style-type: decimal !important;
}

:deep(.editor-content li) {
  margin-bottom: 8px;
}

:deep(.editor-content a) {
  color: var(--primary);
  text-decoration: underline;
}

/* Placeholder styling */
:deep(.editor-content .tiptap p.is-editor-empty:first-child::before),
:deep(.editor-content .tiptap h1.is-empty:first-child::before) {
  content: attr(data-placeholder);
  float: left;
  color: #a1a1aa;
  pointer-events: none;
  height: 0;
}
</style>
